import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Switch,
  FormControlLabel,
  Tooltip
} from '@material-ui/core';
import FilterButtonDropdown from 'components/underwrite/acquisition/propertyUnderwrite/FilterButtonDropdown';
import { ButtonDropdown } from 'components/layout';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { parseISO, format } from 'date-fns';
import {
  setPropertyGridCurrentViewData, setPropertyUWMarkerState, setSelectedCompDataType, toggleIsSelectedAsComp, setPropertyGridRowIdToFocus
} from 'store/actions';
import {
  filteredSelectedOpportunityCompsSelector,
  displayedValuationSelector,
  isUsersValuationSelector
} from 'store/selectors';
import {
  Column,
  Grouping,
  Paging,
  SearchPanel,
  Sorting,
  Selection,
  FilterPanel,
  HeaderFilter,
  ColumnFixing,
  Summary,
  TotalItem,
  FilterBuilder,
  Export
} from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';
import Query from "devextreme/data/query";
import columnSchema from 'components/underwrite/shared/compsColumnSchema';
import { dataGridExtension } from 'components/devExtreme';
import CustomColumnChooser from 'components/customColumnChooser';
import DataGrid from 'components/devExtreme/DataGrid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import 'components/underwrite/shared/UnderwriteGrid.css';

const PropertyUWGrid = ({ selectedOpportunity, viewValuationDialogIsOpen, selectedOpportunityComps,
  setPropertyUWMarkerState, uncapturedMarkers, isInfoBoxOpen, isUsersValuation, isReadonlyMode,
  classes, setPropertyGridCurrentViewData, propertyGridRowIdToFocus, propertyGridCurrentViewData, toggleIsSelectedAsComp,
  selectedCompDataType, setSelectedCompDataType, compsLoadingState, displayedValuationsComps, setPropertyGridRowIdToFocus, 
  selectedOpportunityAmherstIndicative }) => {
  const [filterArray, setFilterArray] = useState([]);
  const [pageIndex, setPageIndex] = useState(null);
  const [pageNavigating, setPageNavigating] = useState(false);//Used to prevent conflict from navigating to a property on a different page and user changing page
  const [isSelectedOnlyFilterApplied, setIsSelectedOnlyFilterApplied] = useState(false);
  const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);
  const grid = useRef(null);
  const selectionEnabled = isUsersValuation && !isReadonlyMode;

  let dataSource = useMemo(() => {
    // console.log('contruct PropertyUWGrid datasource')
    if (selectedOpportunityComps) {
      let filterCaptured = selectedOpportunityComps;
      if (isSelectedOnlyFilterApplied) {
        filterCaptured = selectedOpportunityComps.filter(comp => displayedValuationsComps?.find(vc => vc.compId === comp.id));
      }
      filterCaptured = filterCaptured.filter(element => !uncapturedMarkers.includes(element.id));
      const withParsedDates = filterCaptured.map(opp => {
        const parsedDates = {};
        if (opp.eventDate) parsedDates.eventDate = format(parseISO(opp.eventDate), 'MM/dd/yyyy');
        if (opp.asgTimeStamp) parsedDates.asgTimeStamp = format(parseISO(opp.asgTimeStamp), 'MM/dd/yyyy');
        return {
          ...opp,
          ...parsedDates,
          // showCheckbox: true, // Stitch in dummy column to contain the comp selection checkbox,
        }
      })
      return withParsedDates;
    }
    return null;
  }, [selectedOpportunityComps, displayedValuationsComps, uncapturedMarkers, isSelectedOnlyFilterApplied, selectedCompDataType]);

  let filteredSelectedComps = useMemo(() => {
    if (!selectedOpportunityComps?.length || displayedValuationsComps == null) return null;
    const selectedComps = displayedValuationsComps.map(comp => comp.compId);
    return selectedOpportunityComps.filter(comp => selectedComps.includes(comp.id)).map(comp => comp.id);
  }, [selectedOpportunityComps, displayedValuationsComps]);

  useEffect(() => {
    if (grid.current && selectedOpportunityComps.length) {
      setViewDataFromGridFilter();
      // selectCompsFromProps();
      setPageIndex(grid.current.instance.pageIndex());
    }
  }, [selectedOpportunityComps, isSelectedOnlyFilterApplied, selectedCompDataType]);

  useEffect(() => {
    if (grid.current) {
      const gridRef = grid.current.instance;
      // Reset filters on selected opportunity change
      if (!viewValuationDialogIsOpen) {
        gridRef.clearFilter();
        setFilterArray([]);
      }
    }
  }, [selectedOpportunity]);

  useEffect(() => {
    if (grid.current && propertyGridRowIdToFocus != null) {
      const gridRef = grid.current.instance;
      const dataIndex = propertyGridCurrentViewData.indexOf(propertyGridRowIdToFocus);
      const dataIsUncaptured = uncapturedMarkers.includes(propertyGridRowIdToFocus);
      if (!gridRef.isRowFocused(propertyGridRowIdToFocus) && dataIndex !== -1 && !dataIsUncaptured) {
        gridRef.option("focusedRowKey", propertyGridRowIdToFocus);
        if (gridRef.getRowIndexByKey(propertyGridRowIdToFocus) === -1) {
          setPageNavigating(true);
        }
      } else if (dataIndex === -1 || dataIsUncaptured) {
        gridRef.option("focusedRowIndex", -1);
      }
      setPropertyGridRowIdToFocus(null)
    }
  }, [propertyGridCurrentViewData, propertyGridRowIdToFocus]);

  const setViewDataFromGridFilter = async () => {
    if (grid?.current){
      const gridRef = grid.current.instance;
      const filterValue = gridRef.option('filterValue');
      const filterPanelEnabled = gridRef.option('filterPanel').filterEnabled;
      let visibleFilteredRowHomeIds;
      if (filterValue != null && filterPanelEnabled) {
        const combinedFilter = gridRef.getCombinedFilter();
        visibleFilteredRowHomeIds = Query(dataSource)
          .filter(combinedFilter)
          .toArray()
          .map(row => row.id);
      } else {
        let filterCaptured = selectedOpportunityComps;
        if (isSelectedOnlyFilterApplied) {
          filterCaptured = selectedOpportunityComps.filter(comp => displayedValuationsComps?.find(vc => vc.compId === comp.id));
        }
        visibleFilteredRowHomeIds = filterCaptured.map(row => row.id);
      }
      setPropertyGridCurrentViewData(visibleFilteredRowHomeIds);
    }
  }

  const handleSelectMapDataType = e => {
    if (e.value !== selectedCompDataType) {
      setPropertyUWMarkerState({
        selectedMarker: null,
        isInfoBoxOpen: false,
        isFetchingImages: false,
        selectedMarkerImages: [],
        uncapturedMarkers: []
      });
      setSelectedCompDataType(e.value)
    }
  }

  // const getCellRender = (cell) => {
  //   if (!selectedOpportunity || cell.value === null || cell.value === undefined) {
  //     return null
  //   }

  //   const SourceChip = ({ source }) => {
  //     const chipClass = source === 'MSR' ? 'compSourceMSR' : 'chipTrue';
  //     return <Chip
  //       className={classNames(classes.compTypeChip, classes[chipClass])}
  //       label={cell.value}
  //     />
  //   }

  //   const BoolChip = ({ bool }) => {
  //     if (bool === null) {
  //       return <Chip
  //         className={classNames(classes.compTypeChip, classes.chipFalse)}
  //         label={'N/A'}
  //       />
  //     } else {
  //       const chipClass = bool ? 'chipTrue' : 'chipFalse';
  //       const chipText = bool ? 'True' : 'False';
  //       return <Chip
  //         className={classNames(classes.compTypeChip, classes[chipClass])}
  //         label={chipText}
  //       />
  //     }
  //   }

  //   const checkFieldDiff = (range) => {
  //     const field = cell.column.dataField;
  //     const diff = Math.abs(cell.value - selectedOpportunity[field]);
  //     let isComp = range ? range - diff > 0 : diff === 0
  //     if (isComp) {
  //       return <div className={classes.compText}>
  //         {/* <Chip
  //           className={classes.compChip}
  //           label={cell.value}
  //         /> */}
  //         {cell.text}
  //       </div>
  //     }
  //     return <div>{cell.text}</div>
  //   }

  //   switch (cell.column.dataField) {
  //     case 'showCheckbox':
  //       return <GridCheckbox comp={cell.data} />
  //     // case 'source':
  //     //   return <SourceChip source={cell.value} />
  //     case 'beds':
  //       return checkFieldDiff();
  //     case 'baths':
  //       return checkFieldDiff();
  //     case 'sqft':
  //       return checkFieldDiff(200);
  //     case 'yearBuilt':
  //       return checkFieldDiff(2);
  //     case 'isModelComp':
  //       return <BoolChip bool={cell.value === '1' ? true : cell.value === '0' ? false : null} />
  //     case 'truliaUrl':
  //       return <div><a href={cell.value} target='_blank'>View on Trulia</a></div>
  //     case 'zillowUrl':
  //       return <div><a href={cell.value} target='_blank'>View on Zillow</a></div>
  //     case 'masterCompTypeLabel':
  //       {
  //         setTimeout(() => {
  //           if (!cell.cellElement) return;
  //           switch (cell.value.toUpperCase()) {
  //             case 'C':
  //               cell.cellElement.className = 'c';
  //               break;
  //             case 'T1':
  //               cell.cellElement.className = 't1';
  //               break;
  //             case 'T2':
  //               cell.cellElement.className = 't2';
  //               break;
  //           }
  //         }, 100);
  //         return cell.value;
  //       }
  //     case 'modelCompTypeLabel':
  //       {
  //         if (cell.value.toUpperCase() === 'BELOW MEDIAN' || cell.value.toUpperCase() === 'MEDIAN') {
  //           let rowEl = cell.component.getRowElement(cell.rowIndex);
  //           if (rowEl) {
  //             if (rowEl[0]) {
  //               rowEl[0].classList.add('median');
  //             }
  //             if (rowEl[1]) {
  //               rowEl[1].classList.add('median');
  //             }
  //           }
  //         }
  //         return cell.value;
  //       }
  //     default:
  //       return null;
  //   }
  // }

  let columns = useMemo(() => {
    return columnSchema.map(({ field, displayName, width, type, format, visible, sortIndex, sortOrder, headerFilter, allowSorting, allowFiltering }) => {
      let fixed = ['source'].includes(field);
      // let cellRender = null;
      // if (['showCheckbox', 'source', 'beds', 'baths', 'sqft', 'yearBuilt', 'isModelComp', 'truliaUrl', 'zillowUrl', 'masterCompTypeLabel', 'modelCompTypeLabel'].includes(field)) {
      // if (['masterCompTypeLabel', 'modelCompTypeLabel'].includes(field)) {
        // cellRender = getCellRender;
      // }
      const shouldShow = !(['growthAdjustedPrice', 'growthAdjustedPricePerSqft'].includes(field) && selectedCompDataType === 'sale');
      return (
        <Column
          key={field}
          dataField={field}
          caption={displayName}
          fixed={fixed}
          width={width}
          dataType={type}
          format={format}
          allowSorting={allowSorting}
          allowFiltering={allowFiltering}
          allowGrouping={true}
          visible={shouldShow && visible !== false}
          sortIndex={sortIndex}
          sortOrder={sortOrder}
          // cellRender={cellRender}
          showInColumnChooser={shouldShow}
        >
          {allowFiltering ?
          <HeaderFilter dataSource={(options) => dataGridExtension.headerFilterSort(options, dataSource, field, type, headerFilter)} />
          : null}
          {/* <ColumnHeaderFilter dataSource={dataGridExtension.headerFilterSort(dataSource, field, type, headerFilter)} /> */}
        </Column>
      )
    })
  }, [dataSource, selectedCompDataType]);

  const onToolbarPreparing = (e) => {
    e.toolbarOptions.items.unshift({
      location: 'before',
      template: 'compTypeDropdown'
    }, {
      location: 'before',
      template: 'filterDropdown'
      }, {
        location: 'before',
        template: 'selectedFilter'
    }, {
      location: 'before',
      template: 'clearFilter'
    }, {
      location: 'before',
      template: 'selectedOnlyFilter'
    });
    // this adds a column chooser icon so we can customize its behaviour
    e.toolbarOptions.items.unshift({
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'columnchooser',
        elementAttr: {
          id: 'propertyUWGridColumnChooserBtn'
        },
        onClick: () => setIsColumnChooserVisible(true)
      }
    });
  }

  const toolbarItemRender = (e) => {
    switch (e.template) {
      case 'compTypeDropdown':
        return compTypeDropdownTemplate();
      case 'filterDropdown':
        return filterDropdownTemplate();
      case 'selectedFilter':
        return selectedFilterTemplate();
      case 'clearFilter':
        return clearFilterTemplate();
      case 'selectedOnlyFilter':
        return toggleSelectedOnlyFilterTemplate();
      default:
        return;
    }
  }

  const compTypeDropdownTemplate = () => {
    const mapDataTypeOptions = [
      { value: 'rent', label: 'Rent' },
      { value: 'sale', label: 'For Sale' },
      { value: 'sold', label: 'Sold' }
    ];
    return (
      <ButtonDropdown
        title={`Comp Type: ${mapDataTypeOptions.find(item => item.value === selectedCompDataType).label}`}
        onSelect={handleSelectMapDataType}
        items={mapDataTypeOptions.map(item => {
          return {
            text: item.label,
            value: item.value,
            disabled: item.disabled
          }
        })}
        menuWidth={150}
        size='small'
        style={{ margin: 0 }}
      />
    )
  }

  const filterDropdownTemplate = () => {
    let toolbarDropdownMenuItems = [];
    if (selectedOpportunity) {

      toolbarDropdownMenuItems = [
        // {
        //   text: `Clear Filters (${this.state.filterArray.length})`
        // },
        // {
        //   separator: true
        // },
        {
          text: 'Same Beds as Opportunity',
          rule: [['beds', '=', selectedOpportunity.beds]]
        },
        {
          text: 'Less than 2 Full Baths',
          rule: [['baths', '<', 2]]
        },
        {
          text: 'Within 0.25 miles',
          rule: [['distance', '<=', 0.25]]
        },
        {
          text: 'Within 0.5 miles',
          rule: [['distance', '<=', 0.5]]
        },
        {
          text: 'Within 1 mile',
          rule: [['distance', '<=', 1]]
        },
        {
          text: '+/- 500 Sqft',
          rule: [['livingArea', 'between', [selectedOpportunity.sqft - 500, selectedOpportunity.sqft + 500]]]
        },
        {
          text: '+/- 10 Years',
          rule: [['yearBuilt', 'between', [selectedOpportunity.yearBuilt - 10, selectedOpportunity.yearBuilt + 10]]]
        },
        {
          text: 'MSR Master Status = Stabilized Leased',
          rule: [['masterStatus', '=', 'Stabilized Leased']]
        },
        {
          text: 'MSR Only',
          rule: [['source', '=', 'MSR']]
        },
        {
          text: 'Street Name is same as Opportunity',
          rule: [['streetAddress', 'contains', selectedOpportunityAmherstIndicative?.streetName]]
        },
        {
          text: 'Census Tract same as Opportunity',
          rule: [['censusTractGeoId', '=', selectedOpportunityAmherstIndicative?.censusTractGeoId]]
        }
      ];
    }

    return (
      <FilterButtonDropdown onSelect={addOrRemoveFilter} items={toolbarDropdownMenuItems} filterArray={filterArray} />
    );
  }

  const selectedFilterTemplate = () => {
    const visibleIds = selectedOpportunityComps.filter(comp => displayedValuationsComps?.find(vc => vc.compId === comp.id) && !uncapturedMarkers.includes(comp.id)).map(comp => comp.id);
    return (
      visibleIds.length ? 
        <div>
          <Tooltip title='Filter to selected row IDs' placement='bottom'>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={() => handleSelectedFilter(visibleIds)}
            >
              Filter Selected
            </Button>
          </Tooltip>
        </div>
      : null
    )
  }

  const clearFilterTemplate = () => {
    return (
      grid?.current?.instance?.option('filterValue') ?
        <div>
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={clearFilter}
          >
            Clear Filter
          </Button>
        </div> :
        null
    )
  }

  const toggleSelectedOnlyFilterTemplate = () => {
    return (
      <div>
        <Tooltip title='Show only actively selected rows' placement='bottom'>
          <FormControlLabel
            control={<Switch checked={isSelectedOnlyFilterApplied} onChange={() => setIsSelectedOnlyFilterApplied(!isSelectedOnlyFilterApplied)} />}
            label="Selected Only"
            className={classes.selectedOnlyFilterSwitch}
          />
        </Tooltip>
      </div>
    )
  }

  const handleSelectedFilter = (visibleIds) => {
    const gridRef = grid.current.instance;
    gridRef.option('filterValue', ['id', 'anyof', visibleIds]);
  }

  const clearFilter = () => {
    const gridRef = grid.current.instance;
    gridRef.clearFilter();
  }

  // const appliedFiltersTemplate = () => {
  //   return (
  //     <div>
  //       {filterArray.map(item => {
  //         return (
  //           <Chip key={item.filter} label={item.filter} onDelete={() => addOrRemoveFilter(item)}
  //             classes={{ deleteIcon: classes.chipDeleteIcon, root: classes.chip }} color='primary' size='small' />
  //         )
  //       })}
  //     </div>
  //   )
  // }

  const addOrRemoveFilter = (item) => {
    const gridRef = grid.current.instance;
    if (item.text.startsWith('Clear Filters')) {
      //gridRef.clearFilter();
      setFilterArray([]);
      gridRef.option('filterValue', null);
    } else if (item.rule) {
      const newRules = item.rule;
      gridRef.option('filterValue', newRules);
    }
  }

  const onFocusedRowChanging = (e) => {
    if (e?.rowElement?.[0]?.attributes?.['aria-rowindex']?.value != null) {// crazy, but e.row doesnt match e.rowElement
      let rowIndex = (parseInt(e.rowElement[0].attributes['aria-rowindex'].value, 10) - 1) % e.component.getDataSource().pageSize();
      let rowData = e.component.getVisibleRows()[rowIndex].data
      setPropertyUWMarkerState({ isFetchingImages: isInfoBoxOpen, selectedMarker: rowData });
    }
  }

  const onPageChange = () => {//datagrid does not have this event
    if (!pageNavigating) {
      setPropertyUWMarkerState({ isFetchingImages: false, isInfoBoxOpen: false, selectedMarkerImages: [], selectedMarker: null });
    } else {
      setPageNavigating(false);
    }
  }

  const onContentReady = (e) => {
    if (grid.current) {
      const gridRef = grid.current.instance;
      if (gridRef.pageIndex() !== pageIndex) {
        setPageIndex(gridRef.pageIndex());
        onPageChange();
      }
    }
  }

  const onOptionChanged = e => {
    if (['filterValue', 'filterPanel'].includes(e.name)) { // If filter changed, update visible rows so map can pick up on it
      setViewDataFromGridFilter();
    }
  }

  const onSelectionChanged = e => {
    if (!filteredSelectedComps || !selectionEnabled) return;
    const { selectedRowKeys } = e;
    const sorted = filteredSelectedComps.sort();
    const isInitialSelection = selectedRowKeys.length === filteredSelectedComps.length && selectedRowKeys.sort().every((el, i) => el === sorted[i]);
    const changedComp = e.currentSelectedRowKeys[0] ?? e.currentDeselectedRowKeys[0];
    if (isInitialSelection || changedComp == null) return;
    toggleIsSelectedAsComp(changedComp);
  }

  const checkFieldDiff = (field, cell, range) => {
    const diff = Math.abs(cell.value - selectedOpportunity[field]);
    let isComp = range ? range - diff > 0 : diff === 0
    if (isComp) {
      cell.cellElement.className = 'compText';
    }
  }

  const onCellPrepared = cell => {
    if (!selectionEnabled && cell.column.type === 'selection' && cell.cellElement.firstElementChild) { // Disable selection when not user's underwrite by adding disabled styles and removing click events
      cell.cellElement.style.pointerEvents = 'none';
      cell.cellElement.firstElementChild.style.opacity = 0.4;
      cell.cellElement.firstElementChild.style.filter = 'grayscale(1)';
    }

    if (cell.rowType === 'data') {
      switch (cell.column.dataField) {
        case 'source':
          cell.cellElement.className = cell.value === 'MSR' ? 'msr' : 'zillow';
          break;
        case 'beds':
          if (checkFieldDiff(cell.column.dataField, cell)) cell.cellElement.className = 'compText';
          break;
        case 'baths':
          checkFieldDiff(cell.column.dataField, cell);
          break;
        case 'livingArea':
          checkFieldDiff('sqft', cell, 200);
          break;
        case 'yearBuilt':
          checkFieldDiff(cell.column.dataField, cell, 2);
          break;
        case 'masterCompTypeLabel':
          {
            switch (cell.value?.toUpperCase()) {
              case 'C':
                cell.cellElement.className = 'c';
                break;
              case 'T1':
                cell.cellElement.className = 't1';
                break;
              case 'T2':
                cell.cellElement.className = 't2';
                break;
              default:
                break;
            }
            break;
          }
        case 'modelCompTypeLabel':
          {
            setTimeout(() => {
              if (cell.value?.toUpperCase() === 'BELOW MEDIAN' || cell.value?.toUpperCase() === 'MEDIAN') {
              let rowEl = cell.component.getRowElement(cell.rowIndex);
              if (rowEl) {
                if (rowEl[0]) {
                  rowEl[0].classList.add('median');
                }
                if (rowEl[1]) {
                  rowEl[1].classList.add('median');
                }
              }
            }
          }, 100)
            break;
          }
        default:
          break;
      }
    }
  }

  const handleContextMenu = e => {
    if (e.target === 'content') {
      // e.items can be undefined
      if (!e.items) e.items = [];
      if (e.row) {
        const { rowType } = e.row;
        if (rowType === 'data') {
          e.items = getContextItems(e);
        }
      }
    }
  }

  const getContextItems = (e) => {
    const cellValue = findNestedValue(e.row.data, e.column.dataField);
    const items = [];

    // Copy cell and row
    items.push({
      text: `Copy cell`,
      onItemClick: () => {
        copyToClipboard(cellValue);
      },
      beginGroup: true,
      disabled: cellValue == null
    });
    items.push({
      text: `Copy row`,
      onItemClick: () => {
        const visibleColumns = grid.current.instance.getVisibleColumns().filter(col => col.dataField);
        const headerString = visibleColumns.map(col => col.caption).join('\t') + '\r\n';
        const rowValues = [];
        visibleColumns.forEach(col => {
          const value = findNestedValue(e.row.data, col.dataField);
          rowValues.push(value == null ? '\xa0' : value);
        });
        const combinedString = headerString + rowValues.join('\t') + '\r\n';
        copyToClipboard(combinedString);
      }
    });

    if (e.row.data.truliaUrl) {
      items.push({
        text: `View on Trulia`,
        onItemClick: () => {
          window.open(e.row.data.truliaUrl, '_blank');
        },
        beginGroup: true
      });
    }
    if (e.row.data.zillowUrl) {
      items.push({
        text: `View on Zillow`,
        onItemClick: () => {
          window.open(e.row.data.zillowUrl, '_blank');
        },
        beginGroup: !e.row.data.truliaLink
      });
    }

    return items;
  }

  const findNestedValue = (row, dataField) => {
    if (!dataField) return undefined;
    let value = row;
    const split = dataField.split('.').filter(el => el !== '');

    for (const el of split) {
      if (value.hasOwnProperty(el)) {
        value = value[el];
      } else {
        value = undefined;
        break;
      }
    };
    return value;
  }

  const copyToClipboard = (string) => {
    const dummy = document.createElement('textarea');
    document.body.appendChild(dummy);
    dummy.value = string;
    dummy.select();
    document.execCommand('copy');
    document.body.removeChild(dummy);
  }

  // const hideColumnChooser = () => {
  //   if (grid.current?.instance) {
  //     grid.current.instance.hideColumnChooser();
  //   }
  // }

const gridFilteredSelectedComps = useMemo(() => { // Pass a new array to grid because it will mutate it. Memoize to prevent issues where selection gets reset on rerender
  return filteredSelectedComps ? [...filteredSelectedComps] : null;
}, [filteredSelectedComps]);

const checkExportFieldDiff = (field, cell, range) => {
  const diff = Math.abs(cell.value - selectedOpportunity[field]);
  let isComp = range ? range - diff > 0 : diff === 0
  if (isComp) {
    cell.font = { color: { argb: '00a400' }, bold: true };
  }
}

const onCompsExporting = (e) => {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet('Main sheet');
  const timestamp = format(new Date(), '_yyyy-MM-dd_HHmmss');
  const fileName = `ReVUe_Acquisition_UW_${selectedCompDataType.charAt(0).toUpperCase()}${selectedCompDataType.substring(1)}_Comps_${timestamp}`;
  exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      customizeCell: ({ gridCell, excelCell }) =>  {
        if (gridCell.rowType === 'data') {
          switch (gridCell.column.dataField) {
            case 'source':
              excelCell.font = { color: { argb: 'FFFFFF' } };
              if (gridCell.value === 'MSR') {
                excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '118f89' } };
              } else {
                excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '004990' } };
              }
              break;
            case 'beds':
              checkExportFieldDiff(gridCell.column.dataField, excelCell);
              break;
            case 'baths':
              checkExportFieldDiff(gridCell.column.dataField, excelCell);
              break;
            case 'livingArea':
              checkExportFieldDiff('sqft', excelCell, 200);
              break;
            case 'yearBuilt':
              checkExportFieldDiff(gridCell.column.dataField, excelCell, 2);
              break;
            case 'masterCompTypeLabel':
              {
                switch (gridCell.value?.toUpperCase()) {
                  case 'C':
                    excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'e7e7e7' } };
                    break;
                  case 'T1':
                    excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9bff9b' } };
                    break;
                  case 'T2':
                    excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'cee6ff' } };
                    break;
                  default:
                    break;
                }
                break;
              }
            case 'modelCompTypeLabel':
              {
                if (gridCell.value?.toUpperCase() === 'BELOW MEDIAN' || gridCell.value?.toUpperCase() === 'MEDIAN') {
                  excelCell._row._cells.forEach((a, index) => {if (index > 0) a.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9bff9b' } }});
                }
              }
            break;
            case 'compScore': {
              if (gridCell.data.modelCompTypeLabel?.toUpperCase() === 'BELOW MEDIAN' || gridCell.data.modelCompTypeLabel?.toUpperCase() === 'MEDIAN') {
                excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9bff9b' } };
              }
            }
              
            default:
              break;
          }
        }
      } 
  }).then(function() {
      workbook.xlsx.writeBuffer()
          .then(function(buffer) {

              saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
          });
  });
}


  return (
    <>
      {/* <Typography variant='h6' style={{ marginTop: -8, fontSize: '1rem' }}>Comparables</Typography> */}
      <div className={classes.container} id='compsGridContainer'>
        <DataGrid
          id={'propertyUWGrid'}
          ref={grid}
          persistState={!viewValuationDialogIsOpen}
          dataSource={dataSource}
          keyExpr='id'
          allowColumnReordering={true}
          allowColumnResizing={true}
          columnResizingMode='widget'
          showRowLines={true}
          showColumnLines={true}
          // height={350}
          height={420}
          width={'100%'}
          hoverStateEnabled={true}
          onContextMenuPreparing={handleContextMenu}
          onSelectionChanged={onSelectionChanged}
          columnAutoWidth={true}
          onContentReady={onContentReady}
          focusedRowEnabled={true}
          selectedRowKeys={gridFilteredSelectedComps}
          onToolbarPreparing={onToolbarPreparing}
          onFocusedRowChanging={onFocusedRowChanging}
          onOptionChanged={onOptionChanged}
          onCellPrepared={onCellPrepared}
        //filterValue={this.state.filterValue}
          noDataText={compsLoadingState.message ?? 'No data'}
          enableTooltips={true}
          tooltips={columnSchema}
          onExporting={onCompsExporting}
        >
          <Export enabled={true} texts={{ exportAll: 'Export visible rows to Excel' }} />
          <Selection mode={'multiple'} allowSelectAll={false} showCheckBoxesMode="always" allowColumnResizing={true} allowFiltering={true} allowSorting={true} />
          <ColumnFixing enable={true} />
          <FilterPanel visible={true} />
          <FilterBuilder fields={dataGridExtension.sortFields(columnSchema)}>
            {dataGridExtension.filterBuilderCustomOperations}
          </FilterBuilder>
          {/* <FilterBuilderPopup /> */}
          <HeaderFilter visible={true} search={{ enabled: true }} hideSelectAllOnSearch={false} />
          <Sorting mode={'multiple'} />
          <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
          <Grouping />
          {/* <Scrolling rowRenderingMode='virtual'/> */}
          <Paging defaultPageSize={50} />
          {/* <StateStoring
            enabled={false}
            type={'custom'}
            storageKey={'storage'}
            customSave={state => console.log(state)}
          /> */}
          <Template name='compTypeDropdown' render={toolbarItemRender} />
          <Template name='filterDropdown' render={toolbarItemRender} />
          <Template name='selectedFilter' render={toolbarItemRender} />
          <Template name='clearFilter' render={toolbarItemRender} />
          <Template name='selectedOnlyFilter' render={toolbarItemRender} />
          {/* <Template name='appliedFilters' render={this.toolbarItemRender} /> */}
          {columns}
          <Summary>
            <TotalItem
              column={'source'}
              summaryType={'count'} />
            <TotalItem
              column={'growthAdjustedPrice'}
              summaryType={'avg'}
              valueFormat={'currency'} />
            <TotalItem
              column={'growthAdjustedPricePerSqft'}
              summaryType={'avg'}
              valueFormat={{ type: 'currency', precision: 2 }} />
          </Summary>
        </DataGrid>
        <CustomColumnChooser
          container="#propertyUWGrid"
          visible={isColumnChooserVisible}
          onHiding={() => setIsColumnChooserVisible(false)}
          columns={columns}
          gridRef={grid}
        />
      </div>
    </>
  )
};

const styles = theme => ({
  container: {
    height: '100%'
  },
  button: {
    margin: theme.spacing(1)
  },
  chip: {
    height: 24,
    color: '#fff',
    marginLeft: 4,
    marginRight: 4,
    backgroundColor: '#004990'
  },
  chipDeleteIcon: {
    fontSize: 18
  },
  aggregateFooterText: {
    whiteSpace: 'nowrap'
  },
  compChip: {
    height: 18,
    color: '#fff',
    backgroundColor: 'rgb(57, 187, 57)'
  },
  compText: {
    color: 'rgb(0, 187, 0)',
    fontWeight: 700
  },
  compTypeChip: {
    height: 18,
    //width: 36,
    color: '#fff'
  },
  compSourceMSR: {
    backgroundColor: '#118f89'
  },
  chipTrue: {
    backgroundColor: '#004990'
  },
  chipFalse: {
    backgroundColor: 'rgba(0, 0, 0, 0.26)'
  },
  selectedOnlyFilterSwitch: {
    marginLeft: '4px'
  }
});

const mapStateToProps = (state) => {
  let displayedValuationsComps = displayedValuationSelector(state)?.valuationsComps;
  return {
    selectedOpportunityComps: filteredSelectedOpportunityCompsSelector(state),
    uncapturedMarkers: state.propertyUWState.propertyUWMarkerState.uncapturedMarkers,
    isInfoBoxOpen: state.propertyUWState.propertyUWMarkerState.isInfoBoxOpen,
    propertyGridCurrentViewData: state.propertyUWState.propertyGridCurrentViewData,
    selectedCompDataType: state.propertyUWState.selectedCompDataType,
    viewValuationDialogIsOpen: state.auctionUWState.viewValuationDialogIsOpen,
    selectedOpportunity: state.propertyUWState.propertyUWClientTxnOpp,
    compsLoadingState: state.propertyUWState.compsLoadingState,
    displayedValuationsComps: displayedValuationsComps,
    isUsersValuation: isUsersValuationSelector(state),
    isReadonlyMode: state.propertyUWState.selectedContainer?.viewingReadonlyMode,
    propertyGridRowIdToFocus: state.propertyUWState.propertyGridRowIdToFocus,
    selectedOpportunityAmherstIndicative: state.propertyUWState.selectedOpportunityAmherstIndicative
  }
};

const mapDispatchToProps = {
  setPropertyUWMarkerState,
  setPropertyGridCurrentViewData,
  setSelectedCompDataType,
  toggleIsSelectedAsComp,
  setPropertyGridRowIdToFocus
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PropertyUWGrid);
